/**
 * Schema.org Structured Data Generators
 *
 * Produces JSON-LD schemas for SoftwareApplication, FAQPage,
 * WebPage, Organization, and BreadcrumbList.
 * Used across all marketing pages for rich snippet eligibility.
 */

const SITE_URL = 'https://rooflyzer.pro';
const LOGO_URL = `${SITE_URL}/images/branding/logo.png`;
const SOCIAL_CARD = `${SITE_URL}/social-card.svg`;

// ──────────────────────────────────────────────
// Organization (reusable across schemas)
// ──────────────────────────────────────────────

export const organizationSchema = {
  '@type': 'Organization',
  name: 'Rooflyzer Inc.',
  url: SITE_URL,
  logo: LOGO_URL,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '30 N Gould St #55327',
    addressLocality: 'Sheridan',
    addressRegion: 'WY',
    postalCode: '82801',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-370-289-8830',
    contactType: 'Customer Service',
    email: 'support@rooflyzer.pro',
  },
  sameAs: ['https://www.linkedin.com/company/rooflyzer'],
};

// ──────────────────────────────────────────────
// SoftwareApplication schema — for feature pages
// ──────────────────────────────────────────────

export interface SoftwareAppSchemaOpts {
  name?: string;
  description: string;
  featureList?: string[];
  screenshot?: string;
  url?: string;
  offers?: {
    price: string;
    priceCurrency?: string;
    description?: string;
  };
  aggregateRating?: {
    ratingValue: string;
    reviewCount: string;
  };
}

export function softwareApplicationSchema(opts: SoftwareAppSchemaOpts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: opts.name || 'Rooflyzer OS',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: opts.description,
    url: opts.url || SITE_URL,
    image: opts.screenshot || SOCIAL_CARD,
    ...(opts.featureList && { featureList: opts.featureList.join(', ') }),
    offers: {
      '@type': 'Offer',
      price: opts.offers?.price || '0',
      priceCurrency: opts.offers?.priceCurrency || 'USD',
      description: opts.offers?.description || 'Free tier available',
    },
    ...(opts.aggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: opts.aggregateRating.ratingValue,
        reviewCount: opts.aggregateRating.reviewCount,
      },
    }),
    publisher: organizationSchema,
  };
}

// ──────────────────────────────────────────────
// FAQPage schema — for FAQ sections on any page
// ──────────────────────────────────────────────

export interface FAQItem {
  question: string;
  answer: string;
}

export function faqPageSchema(faqs: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ──────────────────────────────────────────────
// WebPage schema — generic page-level schema
// ──────────────────────────────────────────────

export interface WebPageSchemaOpts {
  name: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}

export function webPageSchema(opts: WebPageSchemaOpts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: opts.name,
    description: opts.description,
    url: opts.url.startsWith('http') ? opts.url : `${SITE_URL}${opts.url}`,
    ...(opts.datePublished && { datePublished: opts.datePublished }),
    ...(opts.dateModified && { dateModified: opts.dateModified }),
    publisher: organizationSchema,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Rooflyzer OS',
      url: SITE_URL,
    },
  };
}

// ──────────────────────────────────────────────
// Comparison / CompetitorProduct schema
// ──────────────────────────────────────────────

export interface ComparisonSchemaOpts {
  competitorName: string;
  headline: string;
  description: string;
  url: string;
}

export function comparisonPageSchema(opts: ComparisonSchemaOpts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: opts.headline,
    description: opts.description,
    url: opts.url.startsWith('http') ? opts.url : `${SITE_URL}${opts.url}`,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Rooflyzer OS',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
    },
    mentions: {
      '@type': 'SoftwareApplication',
      name: opts.competitorName,
      applicationCategory: 'BusinessApplication',
    },
    publisher: organizationSchema,
  };
}
